import type { DocumentFont } from '@flobi/artwork-core';
import { artworkCmd } from './apiClient';

/**
 * Where the open file lives and what it is made in: its project, the brands linked to
 * that project, and the brand in force — and that brand as the editor uses it.
 *
 * All of it is asked of artwork-back, which asks Projects and Brands on the person's
 * behalf. So a seat that does not open those apps still gets a file that knows its own
 * project, and the brand's own rules still decide whose brand is whose.
 */

export interface ProjectCard {
  readonly id: string;
  readonly name: string;
  readonly color: string | null;
}
export interface BrandCard {
  readonly id: string;
  readonly name: string;
  readonly logoUrl: string | null;
  /** When the brand was last changed in Brands; tells two brands of the same name apart. */
  readonly updatedAt?: string | null;
}
export interface FileContext {
  readonly projectId: string | null;
  /** Whether this person may change the file. */
  readonly canEdit: boolean;
  readonly ownedByMe: boolean;
  /** The file's project, when this person can reach it. */
  readonly project: ProjectCard | null;
  /** The brands linked to that project which this person may use, the first one linked first. */
  readonly brands: readonly BrandCard[];
  /** The brand the file is made in. */
  readonly brand: BrandCard | null;
  /** Whether that was chosen for this file rather than taken from its project. */
  readonly brandChosen: boolean;
}

export interface KitSwatch {
  readonly name: string;
  readonly hex: string;
}
export interface KitTypeStyle {
  readonly role: string;
  readonly family: string;
  readonly weight: number;
  readonly size: number;
  /** As a share of the size, or null to leave it to the face. */
  readonly lineHeight: number | null;
  /** Letter spacing as a share of the size. */
  readonly spacing: number;
  readonly case: 'none' | 'upper' | 'lower';
}
export interface KitPicture {
  readonly name: string;
  readonly url: string;
  /** An SVG: placed as layers, not as a picture. */
  readonly vector: boolean;
  readonly mediaAssetId?: string;
  readonly width?: number;
  readonly height?: number;
  /** The ground the brand shows it on. */
  readonly ground?: string;
  /** Which of the brand's logos this is: Logo, Mark, Wordmark, Monogram or Lockup. Logos only. */
  readonly group?: string;
}
export interface BrandKit {
  readonly id: string;
  readonly name: string;
  readonly logoUrl: string | null;
  /** The brand's paints by group; a `ramp` is steps between two of them, drawn as one run. */
  readonly colours: readonly { name: string; ramp: boolean; swatches: readonly KitSwatch[] }[];
  readonly gradients: readonly { name: string; from: string; to: string; angle: number }[];
  readonly styles: readonly KitTypeStyle[];
  /** One per family of the brand's type whose files a canvas can read. */
  readonly fonts: readonly DocumentFont[];
  readonly graphics: {
    readonly logos: readonly KitPicture[];
    readonly icons: readonly KitPicture[];
    readonly patterns: readonly KitPicture[];
    readonly supergraphics: readonly KitPicture[];
    /** The brand's mascots: a character's sheet, or each shot of one. */
    readonly characters: readonly KitPicture[];
    readonly imagery: readonly KitPicture[];
  };
}

const text = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

function brandCard(value: unknown): BrandCard | null {
  const one = value as Record<string, unknown> | null;
  return one && typeof one.id === 'string' ? { id: one.id, name: text(one.name) ?? 'Brand', logoUrl: text(one.logoUrl), updatedAt: text(one.updatedAt) } : null;
}

/** Read loosely: anything that does not fit is absent, and the editor then behaves as it does for a file with no project. */
function context(answer: unknown): FileContext {
  const one = (answer ?? {}) as { file?: Record<string, unknown>; project?: Record<string, unknown> | null; brands?: unknown; brand?: unknown; brandChosen?: unknown };
  const project = one.project && typeof one.project.id === 'string' ? { id: one.project.id, name: text(one.project.name) ?? 'Project', color: text(one.project.color) } : null;
  return {
    projectId: text(one.file?.projectId),
    canEdit: one.file?.role === 'editor',
    ownedByMe: one.file?.ownedByMe === true,
    project,
    brands: (Array.isArray(one.brands) ? one.brands : []).map(brandCard).filter((brand): brand is BrandCard => brand !== null),
    brand: brandCard(one.brand),
    brandChosen: one.brandChosen === true,
  };
}

export async function fileContext(documentId: string): Promise<FileContext> {
  return context(await artworkCmd('artwork.doc.context', { documentId }));
}

/** A brand's id, null for none on purpose, or 'project' to take the project's again. */
export async function setFileBrand(documentId: string, brandId: string | null | 'project'): Promise<FileContext> {
  return context(await artworkCmd('artwork.doc.setBrand', { documentId, brandId }));
}

/** Every brand this person may use. */
export async function usableBrands(): Promise<BrandCard[]> {
  const rows = await artworkCmd<unknown[]>('artwork.brand.list', {});
  return (Array.isArray(rows) ? rows : []).map(brandCard).filter((brand): brand is BrandCard => brand !== null);
}

const HEX = /^#[0-9a-f]{6}$/i;
const pictures = (value: unknown): KitPicture[] =>
  (Array.isArray(value) ? value : [])
    .filter((one): one is KitPicture => !!one && typeof one.url === 'string' && one.url.startsWith('https://') && typeof one.name === 'string')
    .map((one) => ({ name: one.name, url: one.url, vector: one.vector === true, ...(typeof one.mediaAssetId === 'string' ? { mediaAssetId: one.mediaAssetId } : {}), ...(Number(one.width) > 0 && Number(one.height) > 0 ? { width: Number(one.width), height: Number(one.height) } : {}), ...(HEX.test(String(one.ground)) ? { ground: one.ground } : {}), ...(typeof one.group === 'string' && one.group ? { group: one.group } : {}) }));

export async function brandKit(brandId: string): Promise<BrandKit> {
  const one = ((await artworkCmd('artwork.brand.kit', { brandId })) ?? {}) as Record<string, unknown>;
  const graphics = (one.graphics ?? {}) as Record<string, unknown>;
  const swatch = (value: unknown): value is KitSwatch => !!value && typeof (value as KitSwatch).name === 'string' && HEX.test(String((value as KitSwatch).hex));
  return {
    id: String(one.id ?? brandId),
    name: text(one.name) ?? 'Brand',
    logoUrl: text(one.logoUrl),
    colours: (Array.isArray(one.colours) ? one.colours : [])
      .map((group: { name?: unknown; ramp?: unknown; swatches?: unknown }) => ({ name: text(group?.name) ?? '', ramp: group?.ramp === true, swatches: (Array.isArray(group?.swatches) ? group.swatches : []).filter(swatch) }))
      .filter((group) => group.swatches.length > 0),
    gradients: (Array.isArray(one.gradients) ? one.gradients : []).filter((g: { from?: unknown; to?: unknown }) => HEX.test(String(g?.from)) && HEX.test(String(g?.to))).map((g: { name?: unknown; from: string; to: string; angle?: unknown }) => ({ name: text(g.name) ?? 'Gradient', from: g.from, to: g.to, angle: Number(g.angle) || 0 })),
    styles: (Array.isArray(one.styles) ? one.styles : []).filter((style: KitTypeStyle) => !!style && typeof style.role === 'string' && typeof style.family === 'string' && Number(style.size) > 0),
    fonts: (Array.isArray(one.fonts) ? one.fonts : []).filter((font: DocumentFont) => !!font && typeof font.family === 'string' && Array.isArray(font.faces) && font.faces.length > 0),
    graphics: { logos: pictures(graphics.logos), icons: pictures(graphics.icons), patterns: pictures(graphics.patterns), supergraphics: pictures(graphics.supergraphics), characters: pictures(graphics.characters), imagery: pictures(graphics.imagery) },
  };
}
